import { Fragment, useEffect, useState } from "react";
import { ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/intl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { errMsg, weekLabel } from "./kpiFormat";
import { CampaignSplit, splitPayload, splitTotal, toSplit } from "./CampaignSplit";

const SpendRow = ({ w, onSaved }) => {
  const [amount, setAmount] = useState(w.amount ?? "");
  const [campaigns, setCampaigns] = useState(w.campaigns || "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [split, setSplit] = useState(() => toSplit(w.by_campaign));
  const [open, setOpen] = useState(false);
  const last = w.history?.[w.history.length - 1];
  const splitDirty = JSON.stringify(splitPayload(split)) !== JSON.stringify(splitPayload(toSplit(w.by_campaign)));
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/kpi/ad-spend/${w.week_start}`, {
        amount: amount === "" ? null : Number(amount), campaigns, reason: reason || "Weekly Meta Ads Manager figure",
        by_campaign: splitPayload(split),
      });
      setReason("");
      toast.success(`Spend saved for ${weekLabel(w)}`, { description: "Refresh that week's draft to update its cost per lead." });
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const dirty = String(amount) !== String(w.amount ?? "") || campaigns !== (w.campaigns || "") || splitDirty;
  const reasonOk = reason.trim() === "" || reason.trim().length >= 3;
  const splitOk = split.every((r) => r.name.trim()) && (!split.length || (amount !== "" && splitTotal(split) <= Number(amount) + 0.004));
  const n = (w.by_campaign || []).length;
  return (
    <Fragment>
    <tr className="border-t border-zinc-100" data-testid={`kpi-spend-row-${w.week_start}`}>
      <td className="py-2 pr-3 font-mono-num text-xs whitespace-nowrap">
        <button type="button" onClick={() => setOpen(!open)} data-testid={`kpi-split-toggle-${w.week_start}`}
          className="inline-flex items-center gap-1 transition-colors hover:text-blue-700" title="Split this week's spend by campaign">
          <ChevronRight className={`h-3.5 w-3.5 text-zinc-400 transition-transform ${open ? "rotate-90" : ""}`} />
          {weekLabel(w, true)}
        </button>
        <div className={`pl-5 text-2xs ${splitOk ? "text-zinc-400" : "text-red-600"}`} data-testid={`kpi-split-hint-${w.week_start}`}>
          {!splitOk ? "campaign split needs fixing" : n ? `split across ${n} campaign(s)` : w.suggested_campaigns?.length ? "split by campaign…" : ""}
        </div>
      </td>
      <td className="py-2 pr-3">
        <div className="relative w-32">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-zinc-400">$</span>
          <Input type="number" min={0} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
            placeholder="not entered" className="h-8 pl-6 font-mono-num" data-testid={`kpi-spend-amount-${w.week_start}`} />
        </div>
      </td>
      <td className="py-2 pr-3"><Input value={campaigns} onChange={(e) => setCampaigns(e.target.value)} className="h-8"
        placeholder="Campaign(s)" data-testid={`kpi-spend-campaigns-${w.week_start}`} /></td>
      <td className="py-2 pr-3"><Input value={reason} onChange={(e) => setReason(e.target.value)} className="h-8"
        placeholder={w.amount === null ? "Weekly Meta Ads Manager figure" : "Reason for change"} data-testid={`kpi-spend-reason-${w.week_start}`} /></td>
      <td className="py-2 pr-3 text-2xs text-zinc-500">{last ? `${last.by} · ${fmtDateTime(last.at)}` : "—"}</td>
      <td className="py-2 text-right"><Button size="sm" variant={dirty ? "default" : "outline"} disabled={busy || !dirty || !reasonOk || !splitOk}
        onClick={save} data-testid={`kpi-spend-save-${w.week_start}`}>Save</Button></td>
    </tr>
    {open && (
      <tr><td colSpan={6} className="pb-3 pl-5">
        <CampaignSplit week={w.week_start} total={amount} split={split} setSplit={setSplit} suggested={w.suggested_campaigns || []} />
      </td></tr>
    )}
    </Fragment>
  );
};

export const AdSpendPanel = () => {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/kpi/ad-spend", { params: { weeks: 12 } }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  return (
    <section className="rounded-lg border border-zinc-200 bg-white p-6 space-y-4" data-testid="kpi-ad-spend-panel">
      <div>
        <h3 className="font-display text-lg font-semibold">Facebook / Meta ad spend</h3>
        <p className="text-sm text-zinc-500">From Ads Manager: set the dates to the reporting week (Saturday – Friday) and copy the
          "Amount spent" of the active campaign(s). Leave blank if not known yet — the report shows N/A, never $0. Open a week to
          split its spend by campaign (optional) and see cost per lead for each campaign in the week detail.</p>
      </div>
      {!rows ? <div className="h-32 animate-pulse rounded bg-zinc-100" /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-2xs uppercase tracking-[0.18em] text-zinc-500">
              <th className="pb-2">Week</th><th className="pb-2">Amount (AUD)</th><th className="pb-2">Campaigns</th>
              <th className="pb-2">Reason</th><th className="pb-2">Last change</th><th /></tr></thead>
            <tbody>{rows.map((w) => <SpendRow key={`${w.week_start}-${w.history?.[w.history.length - 1]?.at || ""}`} w={w} onSaved={load} />)}</tbody>
          </table>
        </div>
      )}
    </section>
  );
};
